/**
 * i18n онбординга агентской панели (DEV-372).
 * Подмешивается в локали через deep-merge в ./index.ts (namespace agents.onboarding.*).
 * ВАЖНО: не использовать литеральные `{`/`}`/`@`/`|` в строках, кроме именованной
 * интерполяции вида {done}/{total} — иначе компилятор vue-i18n падает (SyntaxError).
 */

type LocaleOverride = Record<string, unknown>

export const agentOnboardingMessages: Record<'ru' | 'en' | 'vn', LocaleOverride> = {
  ru: {
    agents: {
      onboarding: {
        wizard: {
          skip: 'Пропустить',
          back: 'Назад',
          next: 'Далее',
          steps: {
            welcome: {
              avatar: 'VP',
              title: 'Добро пожаловать в VINPROP',
              subtitle:
                'Это ваша агентская панель. За 4 шага покажем, как передавать клиентов, отслеживать сделки и получать вознаграждение.',
            },
            transfer: {
              avatar: 'К',
              title: 'Передавайте клиентов в один клик',
              subtitle:
                'Заполняете форму уведомления — клиент закрепляется за вами в CRM на фиксированный срок.',
              bullets: {
                b1: 'Единый формат передачи',
                b2: 'Прозрачное закрепление и срок фиксации',
                b3: 'Защита от дублей',
              },
            },
            reward: {
              avatar: '₽',
              title: 'Следите за сделками и выплатами',
              subtitle:
                'В разделе «Мои сделки» видны стадии ваших сделок, а по каждой — ожидаемая дата вознаграждения.',
            },
            ready: {
              title: 'Всё готово — начнём!',
              subtitle: 'Рекомендуем изучить 5 стратегий VINPROP и передать первого клиента.',
              cta: 'Зарегистрировать клиента',
            },
          },
        },
        tour: {
          skip: 'Пропустить тур',
          back: 'Назад',
          next: 'Далее',
          done: 'Готово',
          progress: '{current} из {total}',
          steps: {
            commission: {
              title: 'Дата вашего вознаграждения',
              description:
                'По каждой сделке здесь видна ожидаемая дата выплаты. Если она ещё не назначена — запрос уйдёт ответственному.',
            },
            meeting: {
              title: 'Встречи по сделке',
              description:
                'Запросите встречу с клиентом или менеджером: выберите формат и удобное время.',
            },
            strategies: {
              title: 'Обучающие материалы',
              description:
                '5 видео по инвестиционным стратегиям VINPROP. Изучите перед активной работой с клиентами.',
            },
          },
        },
        firstSteps: {
          title: 'Первые шаги',
          progress: 'Выполнено {done} из {total}',
          hide: 'Скрыть',
          done: 'Готово',
          items: {
            profile: { title: 'Профиль агента заполнен', subtitle: 'Данные подтверждены' },
            strategies: { title: 'Изучите 5 стратегий VINPROP', subtitle: '5–10 минут' },
            firstClient: {
              title: 'Передайте первого клиента',
              subtitle: 'Закрепится за вами в CRM',
            },
            telegram: {
              title: 'Подключите Telegram-уведомления',
              subtitle: 'Статусы сделок и выплат',
            },
          },
        },
        emptyState: {
          howItWorks: {
            title: 'КАК ЭТО РАБОТАЕТ',
            steps: {
              s1: { title: 'Передайте клиента', subtitle: 'Заполните форму уведомления о клиенте' },
              s2: {
                title: 'Клиент закрепляется за вами',
                subtitle: 'CRM создаёт сделку и назначает менеджера',
              },
              s3: {
                title: 'Следите за стадией и выплатой',
                subtitle: 'Стадия сделки и дата вознаграждения — здесь',
              },
            },
          },
        },
      },
    },
  },
  en: {
    agents: {
      onboarding: {
        wizard: {
          skip: 'Skip',
          back: 'Back',
          next: 'Next',
          steps: {
            welcome: {
              avatar: 'VP',
              title: 'Welcome to VINPROP',
              subtitle:
                "This is your agent panel. In 4 steps we'll show how to refer clients, track deals and receive your commission.",
            },
            transfer: {
              avatar: 'C',
              title: 'Refer clients in one click',
              subtitle:
                'Fill in the referral form — the client is fixed to you in the CRM for a set period.',
              bullets: {
                b1: 'Single referral format',
                b2: 'Transparent fixation and term',
                b3: 'Duplicate protection',
              },
            },
            reward: {
              avatar: '₽',
              title: 'Track deals and payouts',
              subtitle:
                'In “My deals” you can see the stage of each deal and its expected commission payout date.',
            },
            ready: {
              title: "All set — let's start!",
              subtitle:
                'We recommend studying the 5 VINPROP strategies and referring your first client.',
              cta: 'Register a client',
            },
          },
        },
        tour: {
          skip: 'Skip tour',
          back: 'Back',
          next: 'Next',
          done: 'Done',
          progress: '{current} of {total}',
          steps: {
            commission: {
              title: 'Your commission date',
              description:
                "For each deal you can see the expected payout date here. If it isn't set yet, a request goes to the responsible person.",
            },
            meeting: {
              title: 'Deal meetings',
              description:
                'Request a meeting with the client or a manager: choose the format and a convenient time.',
            },
            strategies: {
              title: 'Learning materials',
              description:
                '5 videos on VINPROP investment strategies. Study them before actively working with clients.',
            },
          },
        },
        firstSteps: {
          title: 'First steps',
          progress: 'Completed {done} of {total}',
          hide: 'Hide',
          done: 'Done',
          items: {
            profile: { title: 'Agent profile completed', subtitle: 'Details confirmed' },
            strategies: { title: 'Study the 5 VINPROP strategies', subtitle: '5–10 minutes' },
            firstClient: {
              title: 'Refer your first client',
              subtitle: 'Will be fixed to you in the CRM',
            },
            telegram: {
              title: 'Connect Telegram notifications',
              subtitle: 'Deal and payout statuses',
            },
          },
        },
        emptyState: {
          howItWorks: {
            title: 'HOW IT WORKS',
            steps: {
              s1: { title: 'Refer a client', subtitle: 'Fill in the client referral form' },
              s2: {
                title: 'The client is fixed to you',
                subtitle: 'CRM creates a deal and assigns a manager',
              },
              s3: {
                title: 'Track the stage and payout',
                subtitle: 'Deal stage and commission date are here',
              },
            },
          },
        },
      },
    },
  },
  vn: {
    agents: {
      onboarding: {
        wizard: {
          skip: 'Bỏ qua',
          back: 'Quay lại',
          next: 'Tiếp',
          steps: {
            welcome: {
              avatar: 'VP',
              title: 'Chào mừng đến VINPROP',
              subtitle:
                'Đây là bảng điều khiển đại lý của bạn. Qua 4 bước, chúng tôi sẽ hướng dẫn cách giới thiệu khách, theo dõi giao dịch và nhận hoa hồng.',
            },
            transfer: {
              avatar: 'K',
              title: 'Giới thiệu khách chỉ với một cú nhấp',
              subtitle:
                'Điền biểu mẫu thông báo — khách được gắn cho bạn trong CRM trong thời hạn cố định.',
              bullets: {
                b1: 'Định dạng giới thiệu thống nhất',
                b2: 'Gắn kết minh bạch và thời hạn',
                b3: 'Chống trùng lặp',
              },
            },
            reward: {
              avatar: '₽',
              title: 'Theo dõi giao dịch và thanh toán',
              subtitle:
                'Trong mục «Giao dịch của tôi» hiển thị giai đoạn từng giao dịch và ngày dự kiến nhận hoa hồng.',
            },
            ready: {
              title: 'Sẵn sàng — bắt đầu thôi!',
              subtitle:
                'Chúng tôi khuyên bạn tìm hiểu 5 chiến lược VINPROP và giới thiệu khách đầu tiên.',
              cta: 'Đăng ký khách hàng',
            },
          },
        },
        tour: {
          skip: 'Bỏ qua hướng dẫn',
          back: 'Quay lại',
          next: 'Tiếp',
          done: 'Xong',
          progress: '{current}/{total}',
          steps: {
            commission: {
              title: 'Ngày hoa hồng của bạn',
              description:
                'Với mỗi giao dịch, bạn xem được ngày dự kiến thanh toán ở đây. Nếu chưa được ấn định, yêu cầu sẽ chuyển đến người phụ trách.',
            },
            meeting: {
              title: 'Cuộc họp theo giao dịch',
              description:
                'Yêu cầu gặp khách hàng hoặc quản lý: chọn hình thức và thời gian thuận tiện.',
            },
            strategies: {
              title: 'Tài liệu học tập',
              description:
                '5 video về chiến lược đầu tư VINPROP. Hãy xem trước khi làm việc tích cực với khách hàng.',
            },
          },
        },
        firstSteps: {
          title: 'Những bước đầu',
          progress: 'Hoàn thành {done}/{total}',
          hide: 'Ẩn',
          done: 'Xong',
          items: {
            profile: { title: 'Hồ sơ đại lý đã điền', subtitle: 'Đã xác nhận thông tin' },
            strategies: { title: 'Tìm hiểu 5 chiến lược VINPROP', subtitle: '5–10 phút' },
            firstClient: {
              title: 'Giới thiệu khách đầu tiên',
              subtitle: 'Sẽ được gắn cho bạn trong CRM',
            },
            telegram: {
              title: 'Kết nối thông báo Telegram',
              subtitle: 'Trạng thái giao dịch và thanh toán',
            },
          },
        },
        emptyState: {
          howItWorks: {
            title: 'CÁCH HOẠT ĐỘNG',
            steps: {
              s1: { title: 'Giới thiệu khách', subtitle: 'Điền biểu mẫu thông báo khách' },
              s2: {
                title: 'Khách được gắn cho bạn',
                subtitle: 'CRM tạo giao dịch và phân công quản lý',
              },
              s3: {
                title: 'Theo dõi giai đoạn và thanh toán',
                subtitle: 'Giai đoạn giao dịch và ngày hoa hồng ở đây',
              },
            },
          },
        },
      },
    },
  },
}
